import type { Inschrijfperiode } from "./useInschrijfperiodes";
import i18n from "@/i18n";
import { moment } from "@/i18n/datum";

/**
 * Two instants, not two dates: "31 december" is ambiguous by a day and the server compares to
 * the tick. Unlike `datum.ts`, these values are instants, so the browser's own zone is right.
 */

/** ISO with an offset, to the `yyyy-MM-ddTHH:mm` a `datetime-local` input binds. */
export function naarInvoer(iso: string) {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";

  // Built from the local parts rather than through toISOString, which would shift to UTC and
  // show a beheerder in Amsterdam a deadline an hour or two off what they typed.
  const pad = (value: number) => String(value).padStart(2, "0");
  return (
    `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}` +
    `T${pad(at.getHours())}:${pad(at.getMinutes())}`
  );
}

/** Back the other way: the input's local wall time as an instant the API can compare. */
export function naarIso(invoer: string) {
  const at = new Date(invoer);
  return Number.isNaN(at.getTime()) ? "" : at.toISOString();
}

export function momentLang(iso: string) {
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? iso : moment(at);
}

/**
 * `isOpen` is the server's, because that is the clock the gate refuses on. Only the reason a
 * closed window is closed is worked out here, and both halves still say "not open".
 */
export function vensterStatus(periode: Inschrijfperiode) {
  if (periode.isOpen) {
    return {
      open: true,
      gesloten: false,
      label: i18n.t("inschrijfperiodes:venster.open", { moment: momentLang(periode.closesOn) }),
    } as const;
  }

  const nogNietOpen = new Date(periode.opensOn).getTime() > Date.now();

  return {
    open: false,

    // The two closed halves are not one state to anything that sends mail: announcing a window
    // that has not opened is giving notice, and announcing one that has shut is a mail nobody
    // can act on.
    gesloten: !nogNietOpen,
    label: nogNietOpen
      ? i18n.t("inschrijfperiodes:venster.opent", { moment: momentLang(periode.opensOn) })
      : i18n.t("inschrijfperiodes:venster.gesloten", { moment: momentLang(periode.closesOn) }),
  } as const;
}

/**
 * The first open window, otherwise the first there is. The fallback matters: a beheerder may
 * still write under a closed one, so nothing would blank the screen where that happens (ADR-0031).
 */
export function huidigePeriode(periodes: Inschrijfperiode[] | undefined) {
  return periodes?.find((periode) => periode.isOpen) ?? periodes?.[0];
}
